import React, { useState, useMemo, useEffect } from "react";
import styles from "./index.module.scss";
import QuestionAccordion from "./QuestionAccordion";
import {
    LISTEN_STEP_CONDITION_CHANGE,
    UNLISTEN_STEP_CONDITION_CHANGE,
} from "../Step2Selector";
import DATA_TYPES from "../DATA_TYPES";

const exampleQuestion = {
    TopicId: "A",
    Topic: "減災",
    Qid: "A1",
    Question: "避難知識的來源:電視",
    AnserType: "A",
    AnserTypeName: "是否型",
};

// selected topic index = 0, 其他 = 減災與整備行為
// selected topic index = 1, 其他 = 臨震行為
// selected topic index = 4, 其他 = 衝擊程度

function Step1Selector({ questions }) {
    const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
    const [currentTopicFilterId, setCurrentTopicFilterId] = useState(null);

    const [questionsState, setQuestionsState] = useState({
        step1Question: null,
        step2Question: null,
        step3Question: null,
    });

    /**
     * @typedef {Object} Question
     * @property {string} Question - The question text
     * @property {string} Qid - The question ID
     *
     * @typedef {Object} QuestionGroup
     * @property {string} TopicId - The topic ID
     * @property {string} Topic - The topic name
     * @property {Question[]} questions - Array of questions in this topic
     *
     * @returns {QuestionGroup[] | null} Array of question groups or null if no questions
     */
    const questionGroups = useMemo(() => {
        if (!questions) return null;

        let groups = questions.reduce((acc, question) => {
            const existingGroup = acc.find(
                (group) => group.TopicId === question.TopicId
            );
            if (existingGroup) {
                existingGroup.questions.push({
                    Question: `${question.Question}`,
                    Qid: question.Qid,
                    AnswerTypeName: question.AnserTypeName,
                });
            } else {
                acc.push({
                    TopicId: question.TopicId,
                    Topic: question.Topic,
                    questions: [
                        {
                            Question: `${question.Question}`,
                            Qid: question.Qid,
                            AnswerTypeName: question.AnserTypeName,
                        },
                    ],
                });
            }
            return acc;
        }, []);

        const SUB_CATEGORIES = {
            A: ["避難知識的來源", "不準備防災包的理由", "其他"],
            B: ["地震後的避難行為", "其他"],
            C: ["其他"],
            D: ["其他"],
            E: ["損失類型", "其他"],
            F: ["其他"],
        };

        let otherCategoryName = "其他";
        if (selectedTopicIndex === 0) {
            otherCategoryName = "減災與整備行為";
        } else if (selectedTopicIndex === 1) {
            otherCategoryName = "臨震行為";
        } else if (selectedTopicIndex === 4) {
            otherCategoryName = "衝擊程度";
        }

        groups.forEach((group) => {
            const subcategories = SUB_CATEGORIES[group.TopicId] || [];

            const currentAllQuestions = group.questions;

            const MAP = {};

            for (const question of currentAllQuestions) {
                const matchingSubCategory = subcategories.find((subCat) =>
                    question.Question.includes(subCat)
                );

                if (matchingSubCategory) {
                    if (!MAP[matchingSubCategory])
                        MAP[matchingSubCategory] = [];
                    MAP[matchingSubCategory].push(question);
                } else {
                    if (!MAP[otherCategoryName]) MAP[otherCategoryName] = [];
                    MAP[otherCategoryName].push(question);
                }
            }

            const otherCategory = MAP[otherCategoryName];
            delete MAP[otherCategoryName];

            const arr = Object.entries(MAP).map(([key, value]) => ({
                Filter: key,
                questions: value,
            }));

            arr.unshift({
                Filter: otherCategoryName,
                questions: otherCategory,
            });

            group.questions = arr;
        });

        return groups;
    }, [questions, selectedTopicIndex]);

    useEffect(() => {
        if (questionGroups && questionGroups.length > 0) {
            setCurrentTopicFilterId(
                questionGroups[selectedTopicIndex].questions[0].Filter
            );
        }
    }, [selectedTopicIndex, questionGroups]);

    useEffect(() => {
        const handleStepConditionChange = (e) => {
            const { step1Question, step2Question, step3Question } = e.detail;
            setQuestionsState({ step1Question, step2Question, step3Question });
        };

        LISTEN_STEP_CONDITION_CHANGE(handleStepConditionChange);

        return () => {
            UNLISTEN_STEP_CONDITION_CHANGE(handleStepConditionChange);
        };
    }, [selectedTopicIndex]);

    useEffect(() => {
        const handleChange = () => {
            const { step1Question, step2Question, step3Question } = questionsState;
            // const submitBtn = document.getElementById("submit-btn");
            const singleValueQuestionsDom = document.querySelectorAll(
                `.question-item[data-type="${DATA_TYPES.數值型單數}"]`
            );

            const questionDoms = document.querySelectorAll(".question-item");

            // reset all questionDoms
            questionDoms.forEach((dom) => {
                dom.classList.remove("disabled");
            });

            if (!step1Question) {
                // reset
                singleValueQuestionsDom.forEach((dom) => {
                    dom.classList.remove("disabled");
                });
                return;
            }

            if (step1Question.AnswerTypeName === DATA_TYPES.數值型單數) {
                singleValueQuestionsDom.forEach((dom) => {
                    dom.classList.add("disabled");
                });
            } else {
                singleValueQuestionsDom.forEach((dom) => {
                    dom.classList.add("disabled");
                });
            }

            if (step1Question) {
                const question1Dom = document.querySelector(
                    `#question-${step1Question.Qid}`
                );
                if (question1Dom) {
                    question1Dom.classList.add("disabled");
                }
            }

            if (step2Question) {
                const question2Dom = document.querySelector(
                    `#question-${step2Question.Qid}`
                );
                if (question2Dom) {
                    question2Dom.classList.add("disabled");
                }
            }

            if (step3Question) {
                const question3Dom = document.querySelector(
                    `#question-${step3Question.Qid}`
                );
                if (question3Dom) {
                    question3Dom.classList.add("disabled");
                }
            }
        };
        handleChange();
    }, [questionsState, selectedTopicIndex]);

    if (!questionGroups) return null;

    return (
        <div className={styles.step1Selector}>
            <h3>STEP1：選擇主題與項目</h3>
            <div className={styles.topicContainer}>
                {questionGroups.map((group, index) => (
                    <button
                        key={group.TopicId}
                        className={`${styles.topicButton} ${
                            selectedTopicIndex === index
                                ? styles.topicButtonActive
                                : ""
                        }`}
                        onClick={() => setSelectedTopicIndex(index)}
                    >
                        {group.Topic}
                    </button>
                ))}
            </div>
            {questionGroups.map((group, groupId) => (
                <div
                    className={`${styles.questionContainer} ${
                        selectedTopicIndex === groupId
                            ? styles.questionContainerActive
                            : ""
                    }`}
                    key={groupId}
                >
                    <h4>項目</h4>
                    {group.questions.map((question, questionId) =>
                        question.Filter === "其他" ? (
                            question.questions.map((q, idx) => (
                                <div
                                    id={`question-${q.Qid}`}
                                    className={`${styles.otherQuestion} question-item`}
                                    data-type={q.AnswerTypeName}
                                    key={`other-${groupId}-${questionId}-${idx}`}
                                    draggable="true"
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData(
                                            "text/plain",
                                            JSON.stringify(q)
                                        );
                                    }}
                                >
                                    {q.Question}
                                </div>
                            ))
                        ) : (
                            <QuestionAccordion
                                key={`${groupId}-${questionId}`}
                                filterName={question.Filter}
                                questions={question.questions}
                                isOpen={
                                    currentTopicFilterId === question.Filter
                                }
                                onHeaderClick={() => {
                                    setCurrentTopicFilterId(question.Filter);
                                }}
                                onClose={() => setCurrentTopicFilterId(null)}
                            />
                        )
                    )}
                </div>
            ))}
        </div>
    );
}

export default Step1Selector;